import { create } from 'zustand';
import { postForm } from '@/shared/api/client';
import { getCookie, removeCookie, setCookie } from '@/shared/lib/cookies';
import { tokenExpiresAtMs } from '@/shared/lib/jwt';

const AUTH_COOKIE = 'admin_auth_token';
const REFRESH_COOKIE = 'admin_refresh_token';

export interface AuthProfile {
  id: number | string | null;
  name: string;
  email: string;
  roles: Record<string, unknown>;
  authToken: string | null;
}

const empty: AuthProfile = { id: null, name: '', email: '', roles: {}, authToken: null };

interface AuthState {
  profile: AuthProfile;
  isBootstrapping: boolean;
  isAuthenticated: boolean;
  bootstrap: () => Promise<boolean>;
  login: (emailOrPhone: string, password: string) => Promise<boolean>;
  logout: () => Promise<void>;
  setToken: (token: string, refresh?: string) => void;
  /** Refresh access token using refresh cookie. Returns new access token or null. */
  refreshAccessToken: () => Promise<string | null>;
}

type InfoResponse = {
  success: number;
  id?: number | string;
  name?: string;
  email?: string;
  roles?: Record<string, unknown>;
};

const loadProfile = async (token: string): Promise<AuthProfile | null> => {
  const res = await postForm<InfoResponse>('/user/info/', { auth_token: token });
  if (res.success !== 1) return null;
  return {
    id: res.id ?? null,
    name: res.name ?? '',
    email: res.email ?? '',
    roles: res.roles ?? {},
    authToken: token,
  };
};

export const useAuthStore = create<AuthState>((set, get) => ({
  profile: empty,
  isBootstrapping: true,
  isAuthenticated: false,

  setToken: (token, refresh) => {
    setCookie(AUTH_COOKIE, token);
    if (refresh) setCookie(REFRESH_COOKIE, refresh);
    set({ profile: { ...get().profile, authToken: token }, isAuthenticated: true });
  },

  logout: async () => {
    const token = getCookie(AUTH_COOKIE);
    if (token) {
      try {
        await postForm('/user/logout/', { auth_token: token });
      } catch {
        /* ignore */
      }
    }
    removeCookie(AUTH_COOKIE);
    removeCookie(REFRESH_COOKIE);
    set({ profile: empty, isAuthenticated: false, isBootstrapping: false });
  },

  refreshAccessToken: async () => {
    const refresh = getCookie(REFRESH_COOKIE);
    if (!refresh) return null;
    try {
      const res = await postForm<{
        success: number | boolean;
        access_token?: string;
        auth_token?: string;
        refresh_token?: string;
      }>('/user/refresh-token/', { refresh_token: refresh });
      const token = res.access_token || res.auth_token;
      if (res.success !== 1 || !token) return null;
      get().setToken(token, res.refresh_token);
      return token;
    } catch {
      return null;
    }
  },

  bootstrap: async () => {
    set({ isBootstrapping: true });
    let token = getCookie(AUTH_COOKIE);

    // Proactively refresh if access token is missing or about to expire
    const expiresAt = tokenExpiresAtMs(token);
    if (!token || (expiresAt > 0 && expiresAt - Date.now() <= 60_000)) {
      token = (await get().refreshAccessToken()) || token;
    }

    if (!token) {
      set({ isBootstrapping: false, isAuthenticated: false, profile: empty });
      return false;
    }

    try {
      let profile = await loadProfile(token);
      if (!profile) {
        const refreshed = await get().refreshAccessToken();
        if (refreshed) profile = await loadProfile(refreshed);
      }
      if (!profile) {
        await get().logout();
        return false;
      }
      set({ isBootstrapping: false, isAuthenticated: true, profile });
      return true;
    } catch {
      // Network blip: keep session if we still have a token
      set({
        isBootstrapping: false,
        isAuthenticated: true,
        profile: { ...get().profile, authToken: token },
      });
      return true;
    }
  },

  login: async (emailOrPhone, password) => {
    removeCookie(AUTH_COOKIE);
    removeCookie(REFRESH_COOKIE);
    const res = await postForm<{
      success: number | boolean;
      auth_token?: string;
      access_token?: string;
      refresh_token?: string;
    }>('/user/auth/', { email_or_phone: emailOrPhone, password });
    const token = res.auth_token || res.access_token;
    if (res.success !== 1 || !token) return false;
    get().setToken(token, res.refresh_token);
    return get().bootstrap();
  },
}));
